import { ID, SuccessApiResponse } from '@ianesthesie-kit/services';
import { doctorBffApi } from '../doctorBffApi.slice';
import { wrapError } from '../error';
import {
  BillingEntity,
  BillingEntityMember,
  HostedLink,
  OnboardingLinkPayload,
  RegisterReaderPayload,
  TerminalReader
} from './billingEntity.model';
import { BillingEntityService } from './billingEntity.service';

type EntityScoped = { entityId: ID };

/**
 * Assigning or unassigning a doctor also changes the entity list: `currentDoctorMember` gates
 * the Stripe dashboard link of the calling doctor.
 */
const MEMBER_TAGS = ['BillingEntityMembers', 'BillingEntities'] as const;

export const billingEntityApi = doctorBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    billingEntities: builder.query<BillingEntity[], void>({
      providesTags: (res, err) => (err ? [] : ['BillingEntities']),
      queryFn: () =>
        BillingEntityService.fetchEntities()
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    refreshBillingEntity: builder.mutation<BillingEntity, EntityScoped>({
      invalidatesTags: (res, err) => (err ? [] : ['BillingEntities']),
      queryFn: ({ entityId }) =>
        BillingEntityService.refreshEntity(entityId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    // Minting a link does not invalidate the list: the page that asks for one immediately hands
    // its tab to Stripe, and the account status it changes is picked up by the settings page's
    // focus refetch when the doctor comes back.
    createOnboardingLink: builder.mutation<
      HostedLink,
      EntityScoped & { payload: OnboardingLinkPayload }
    >({
      queryFn: ({ entityId, payload }) =>
        BillingEntityService.createOnboardingLink(entityId, payload)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    createLoginLink: builder.mutation<HostedLink, EntityScoped>({
      queryFn: ({ entityId }) =>
        BillingEntityService.createLoginLink(entityId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    billingEntityMembers: builder.query<BillingEntityMember[], void>({
      providesTags: (res, err) => (err ? [] : ['BillingEntityMembers']),
      queryFn: () =>
        BillingEntityService.fetchMembers()
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    assignDoctor: builder.mutation<
      SuccessApiResponse,
      EntityScoped & { doctorId: ID }
    >({
      invalidatesTags: (res, err) => (err ? [] : [...MEMBER_TAGS]),
      queryFn: ({ entityId, doctorId }) =>
        BillingEntityService.assignDoctor(entityId, doctorId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    unassignDoctor: builder.mutation<SuccessApiResponse, { doctorId: ID }>({
      invalidatesTags: (res, err) => (err ? [] : [...MEMBER_TAGS]),
      queryFn: ({ doctorId }) =>
        BillingEntityService.unassignDoctor(doctorId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    terminalReaders: builder.query<TerminalReader[], void>({
      providesTags: (res, err) => (err ? [] : ['BillingTerminalReaders']),
      queryFn: () =>
        BillingEntityService.fetchReaders()
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    registerReader: builder.mutation<
      TerminalReader,
      EntityScoped & { payload: RegisterReaderPayload }
    >({
      invalidatesTags: (res, err) => (err ? [] : ['BillingTerminalReaders']),
      queryFn: ({ entityId, payload }) =>
        BillingEntityService.registerReader(entityId, payload)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    deleteReader: builder.mutation<SuccessApiResponse, { readerId: ID }>({
      invalidatesTags: (res, err) => (err ? [] : ['BillingTerminalReaders']),
      queryFn: ({ readerId }) =>
        BillingEntityService.deleteReader(readerId)
          .then((data) => ({ data }))
          .catch(wrapError)
    })
  })
});

export const {
  useBillingEntitiesQuery,
  useRefreshBillingEntityMutation,
  useCreateOnboardingLinkMutation,
  useCreateLoginLinkMutation,
  useBillingEntityMembersQuery,
  useAssignDoctorMutation,
  useUnassignDoctorMutation,
  useTerminalReadersQuery,
  useRegisterReaderMutation,
  useDeleteReaderMutation
} = billingEntityApi;
